import type { Invite, InvitedPerson, InviteType, RsvpResponse } from './types'

/** Short labels for the badge next to each name. */
export const INVITE_TYPE_LABEL: Record<InviteType, string> = {
  day: 'All day',
  evening: 'Evening',
}

/**
 * The answers being edited, keyed by person id.
 *
 * This lives here rather than with the card so that the card and the form can
 * both refer to it without importing from each other.
 */
export type Draft = Record<string, RsvpResponse>

/** The starting draft for an invite: whatever has already been answered. */
export function draftFrom(invite: Invite): Draft {
  return Object.fromEntries(invite.people.map((p) => [p.person_id, p.response]))
}

/** Whether everyone on the invite has already replied. */
export function everyoneAnswered(invite: Invite): boolean {
  return invite.people.every((p) => p.response !== 'waiting')
}

/**
 * Groups a code into fours for reading aloud or printing, matching
 * wedding.FormatCode on the server. Codes are stored and compared without the
 * dash; this is presentation only.
 */
export function formatCode(code: string): string {
  return code.match(/.{1,4}/g)?.join('-') ?? code
}

/**
 * Puts a code the way it is stored, matching wedding.NormalizeCode on the
 * server: upper case, and anything that is not a letter or digit dropped.
 *
 * So "abcd 1234", "abcd-1234" and "ABCD1234" are all the same code, whether a
 * guest typed it or it came out of a link on their invitation. The server does
 * this too and is the authority; this is only so the client can tell a plausible
 * code from a stray path before it bothers asking.
 */
export function normalizeCode(code: string): string {
  return code.toUpperCase().replace(/[^A-Z0-9]/g, '')
}

/** "Alice", "Alice and Bob", "Alice, Bob and Carol". */
export function formatNames(names: readonly string[]): string {
  if (names.length === 0) return ''
  if (names.length === 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

function namesOfType(people: readonly InvitedPerson[], type: InviteType): string[] {
  return people.filter((p) => p.invite_type === type).map((p) => p.name)
}

/** Whether anyone on the invite is invited for the whole day. */
export function hasDayGuests(people: readonly InvitedPerson[]): boolean {
  return people.some((p) => p.invite_type === 'day')
}

/**
 * A sentence telling the invite what it covers, which matters most for
 * households that are a mix of the two.
 */
export function coverageSentence(people: readonly InvitedPerson[]): string {
  const day = namesOfType(people, 'day')
  const evening = namesOfType(people, 'evening')

  // "both" only when there really are two of them.
  const you = people.length === 1 ? 'You are' : people.length === 2 ? 'You are both' : 'You are all'

  if (day.length > 0 && evening.length === 0) {
    return `${you} invited to join us for the whole day.`
  }
  if (evening.length > 0 && day.length === 0) {
    return `${you} invited to join us for the evening celebrations.`
  }
  // Mixed, so name who is invited to what rather than generalising.
  const dayVerb = day.length === 1 ? 'is' : 'are'
  return `${formatNames(day)} ${dayVerb} invited for the whole day, and ${formatNames(evening)} for the evening.`
}
